import React, {useState} from "react"
import PropTypes from "prop-types"
import CloseRoundedIcon from "@mui/icons-material/CloseRounded"
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded"
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded"
import {
    Box,
    CircularProgress,
    Divider,
    Drawer,
    IconButton,
    Link,
    Stack,
    Typography,
} from "@mui/material"
import ButtonWithIcon from "../Button/Button"
import StatusBadge from "../StatusBadge"
import WorkflowErrorPanel, {workflowErrorShape} from "./workflow-error-panel"
import WorkflowProgress from "./workflow-progress"
import {
    formatWorkflowDateTime,
    workflowStatusPresentation,
} from "./workflow-execution"

const StageCard = ({stage}) => {
    const status = workflowStatusPresentation(stage.status)
    const attempt = Math.max(1, Number(stage.attempt ?? 0) + 1)

    return (
        <Box sx={{border: "1px solid", borderColor: "primary.white10", p: 1.5}}>
            <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                <Typography variant="body2" color="common.white" fontWeight={700} sx={{wordBreak: "break-word"}}>
                    {stage.task_id || stage.execution_key || "Etapa"}
                </Typography>
                <StatusBadge label={status.text} backgroundColor={status.color} radius={1}/>
            </Stack>
            <Typography variant="caption" color="primary.white70" display="block" sx={{mt: 1}}>
                Tentativa {attempt}
                {stage.map_index != null ? ` · Item ${stage.map_index}` : ""}
                {stage.duration_seconds != null ? ` · ${stage.duration_seconds}s` : ""}
            </Typography>
            {(stage.source || stage.curve) && (
                <Typography variant="caption" color="primary.white70" display="block">
                    Origem: {stage.source || "—"} · Curva: {stage.curve || "—"}
                </Typography>
            )}
            <Typography variant="caption" color="primary.white70" display="block">
                {formatWorkflowDateTime(stage.started_at)} → {formatWorkflowDateTime(stage.finished_at)}
            </Typography>
            {stage.work_item && (
                <Typography variant="caption" color="primary.white70" display="block" sx={{mt: 0.5}}>
                    Item de trabalho: {typeof stage.work_item === "string" ? stage.work_item : JSON.stringify(stage.work_item)}
                </Typography>
            )}
            {stage.artifact_summary && Object.keys(stage.artifact_summary).length > 0 && (
                <Box
                    component="pre"
                    sx={{
                        mt: 1,
                        mb: 0,
                        p: 1,
                        fontSize: 11,
                        color: "primary.white70",
                        backgroundColor: "primary.white05",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                    }}
                >
                    {JSON.stringify(stage.artifact_summary, null, 2)}
                </Box>
            )}
            {stage.airflow_url && (
                <Link
                    href={stage.airflow_url}
                    target="_blank"
                    rel="noreferrer"
                    color="primary.main2"
                    variant="caption"
                    sx={{display: "inline-block", mt: 1}}
                >
                    Abrir tarefa no Airflow
                </Link>
            )}
        </Box>
    )
}

StageCard.propTypes = {
    stage: PropTypes.shape({
        execution_key: PropTypes.string,
        task_id: PropTypes.string,
        map_index: PropTypes.number,
        attempt: PropTypes.number,
        status: PropTypes.string,
        duration_seconds: PropTypes.number,
        source: PropTypes.string,
        curve: PropTypes.string,
        work_item: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        artifact_summary: PropTypes.object,
        airflow_url: PropTypes.string,
        started_at: PropTypes.string,
        finished_at: PropTypes.string,
    }).isRequired,
}

const WorkflowExecutionDrawer = ({open, execution, loading, onClose}) => {
    const [copied, setCopied] = useState(false)
    const status = workflowStatusPresentation(execution?.status)

    const copyDiagnostic = async () => {
        if (!execution) return
        const diagnostic = {
            execution_id: execution.id,
            dag_id: execution.dag_id,
            run_id: execution.run_id,
            status: execution.status,
            current_stage: execution.current_stage,
            current_error: execution.current_error,
        }
        await navigator.clipboard.writeText(JSON.stringify(diagnostic, null, 2))
        setCopied(true)
        window.setTimeout(() => setCopied(false), 1500)
    }

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: {xs: "100vw", sm: 620},
                    maxWidth: "100vw",
                    color: "common.white",
                    backgroundColor: "background.paper",
                    backgroundImage: "none",
                },
            }}
        >
            <Box sx={{p: {xs: 2, sm: 3}}}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h3" color="common.white">Detalhes da execução</Typography>
                    <IconButton aria-label="Fechar detalhes" onClick={onClose} sx={{color: "common.white"}}>
                        <CloseRoundedIcon/>
                    </IconButton>
                </Stack>
                <Divider sx={{my: 2, borderColor: "primary.white10"}}/>

                {loading && (
                    <Box sx={{display: "flex", justifyContent: "center", p: 5}}>
                        <CircularProgress/>
                    </Box>
                )}

                {!loading && execution && (
                    <Stack spacing={2.5}>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <StatusBadge
                                label={(execution.contract_version || "v1").toUpperCase()}
                                backgroundColor={execution.contract_version === "v2" ? "#5C6BC0" : "#667085"}
                                radius={1}
                            />
                            <StatusBadge label={status.text} backgroundColor={status.color} radius={1}/>
                        </Stack>

                        <Box>
                            <Typography variant="subtitle1" color="common.white">{execution.dag_id}</Typography>
                            <Typography variant="caption" color="primary.white70" sx={{wordBreak: "break-all"}}>
                                {execution.run_id || "Run não informado"}
                            </Typography>
                        </Box>

                        <WorkflowProgress
                            processed={execution.progress_processed}
                            total={execution.progress_total}
                            percent={execution.progress_percent}
                        />

                        <Stack spacing={0.5}>
                            <Typography variant="body2" color="primary.white70">
                                Etapa atual: <Box component="span" color="common.white">{execution.current_stage || "Não informada"}</Box>
                            </Typography>
                            <Typography variant="body2" color="primary.white70">
                                Usuário: <Box component="span" color="common.white">{execution.created_by_name || execution.created_by || "Não informado"}</Box>
                            </Typography>
                            <Typography variant="body2" color="primary.white70">
                                Heartbeat: <Box component="span" color="common.white">{formatWorkflowDateTime(execution.heartbeat_at)}</Box>
                            </Typography>
                        </Stack>

                        {execution.current_error && (
                            <WorkflowErrorPanel error={execution.current_error}/>
                        )}

                        <Stack direction={{xs: "column", sm: "row"}} spacing={1}>
                            {execution.current_error && (
                                <ButtonWithIcon icon={<ContentCopyRoundedIcon/>} onClick={copyDiagnostic}>
                                    {copied ? "COPIADO" : "COPIAR DIAGNÓSTICO"}
                                </ButtonWithIcon>
                            )}
                            {execution.airflow_url && (
                                <ButtonWithIcon
                                    icon={<OpenInNewRoundedIcon/>}
                                    component="a"
                                    href={execution.airflow_url}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    ABRIR AIRFLOW
                                </ButtonWithIcon>
                            )}
                        </Stack>

                        <Divider sx={{borderColor: "primary.white10"}}/>
                        {!execution.capabilities?.detailed_progress ? (
                            <Typography color="primary.white70">
                                Detalhamento por etapa não disponível para este workflow legado.
                            </Typography>
                        ) : (
                            <Stack spacing={1.5}>
                                <Typography variant="subtitle1" color="common.white">Etapas e tarefas</Typography>
                                {execution.stages.length === 0 && (
                                    <Typography color="primary.white70">Nenhuma etapa registrada.</Typography>
                                )}
                                {execution.stages.map((stage, index) => (
                                    <StageCard
                                        key={`${stage.execution_key || stage.task_id || "stage"}-${stage.map_index ?? "none"}-${index}`}
                                        stage={stage}
                                    />
                                ))}
                            </Stack>
                        )}

                        {execution.errors.length > 0 && (
                            <Stack spacing={1.5}>
                                <Divider sx={{borderColor: "primary.white10"}}/>
                                <Typography variant="subtitle1" color="common.white">Histórico de erros</Typography>
                                {execution.errors.map((error, index) => (
                                    <WorkflowErrorPanel key={error.id || error.fingerprint || index} error={error}/>
                                ))}
                            </Stack>
                        )}
                    </Stack>
                )}
            </Box>
        </Drawer>
    )
}

WorkflowExecutionDrawer.propTypes = {
    open: PropTypes.bool.isRequired,
    loading: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    execution: PropTypes.shape({
        id: PropTypes.number,
        dag_id: PropTypes.string,
        run_id: PropTypes.string,
        contract_version: PropTypes.string,
        status: PropTypes.string,
        current_stage: PropTypes.string,
        progress_processed: PropTypes.number,
        progress_total: PropTypes.number,
        progress_percent: PropTypes.number,
        created_by_name: PropTypes.string,
        created_by: PropTypes.number,
        heartbeat_at: PropTypes.string,
        current_error: workflowErrorShape,
        airflow_url: PropTypes.string,
        capabilities: PropTypes.shape({detailed_progress: PropTypes.bool}),
        stages: PropTypes.array,
        errors: PropTypes.arrayOf(workflowErrorShape),
    }),
}

export default WorkflowExecutionDrawer
